import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import client from '../../api/client';

function FoodDBPage() {
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [editingFood, setEditingFood] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const limit = 50;

  const fetchFoods = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search.trim()) params.q = search.trim();
      const { data } = await client.get('/api/food/admin/list', { params });
      setFoods(data.foods || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch foods:', err);
      setFoods([]);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchFoods();
  }, [fetchFoods]);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleEdit = async (food) => {
    try {
      const { data } = await client.get(`/api/food/admin/${food._id}`);
      const fullFood = data.food;
      // Convert quantities: storedValue = 100 / grams → grams = 100 / storedValue
      const quantitiesGrams = {};
      if (fullFood.quantities) {
        Object.entries(fullFood.quantities).forEach(([unit, conversionFactor]) => {
          quantitiesGrams[unit] = conversionFactor > 0 ? Math.round((100 / conversionFactor) * 100) / 100 : 0;
        });
      }
      setEditForm({
        _id: fullFood._id,
        dish_name: fullFood.dish_name || '',
        calories_kcal: fullFood.calories_kcal || 0,
        protein_g: fullFood.macros?.protein_g || 0,
        carbohydrates_g: fullFood.macros?.carbohydrates_g || 0,
        fat_total_g: fullFood.macros?.fat_total_g || 0,
        sugars_g: fullFood.macros?.sugars_g || 0,
        dietary_fiber_g: fullFood.macros?.dietary_fiber_g || 0,
        quantities: quantitiesGrams,
      });
      setEditingFood(fullFood);
    } catch (err) {
      console.error('Failed to fetch food details:', err);
    }
  };

  const handleSave = async () => {
    if (!editForm) return;
    setSaving(true);
    try {
      // Convert quantities back: grams → storedValue = 100 / grams
      const quantities = {};
      Object.entries(editForm.quantities).forEach(([unit, grams]) => {
        const g = parseFloat(grams);
        quantities[unit] = g > 0 ? Math.round((100 / g) * 10000) / 10000 : 0;
      });

      const payload = {
        dish_name: editForm.dish_name,
        calories_kcal: parseFloat(editForm.calories_kcal),
        macros: {
          protein_g: parseFloat(editForm.protein_g),
          carbohydrates_g: parseFloat(editForm.carbohydrates_g),
          fat_total_g: parseFloat(editForm.fat_total_g),
          sugars_g: parseFloat(editForm.sugars_g),
          dietary_fiber_g: parseFloat(editForm.dietary_fiber_g),
        },
        quantities,
      };

      await client.patch(`/api/food/admin/${editForm._id}`, payload);
      setEditingFood(null);
      setEditForm(null);
      fetchFoods();
    } catch (err) {
      console.error('Failed to save food:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingFood(null);
    setEditForm(null);
  };

  const handleDelete = async (foodId) => {
    setDeletingId(foodId);
    try {
      await client.delete(`/api/food/admin/${foodId}`);
      setConfirmDeleteId(null);
      fetchFoods();
    } catch (err) {
      console.error('Failed to delete food:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const updateField = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateQuantity = (unit, value) => {
    setEditForm((prev) => ({
      ...prev,
      quantities: { ...prev.quantities, [unit]: value },
    }));
  };

  return (
    <div className="min-h-screen bg-black p-4 md:p-8 font-[Inter]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Food Database</h1>
          <p className="text-slate-400">Manage and edit food entries</p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <svg
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search foods by name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-[#12121a] border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
            />
          </div>
          {!loading && (
            <p className="text-slate-500 text-sm mt-2">
              {total} food{total !== 1 ? 's' : ''} found
            </p>
          )}
        </div>

        {/* Table / List */}
        <div className="glass-card rounded-2xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-slate-400">Loading foods...</p>
              </div>
            </div>
          ) : foods.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <svg
                  className="w-12 h-12 text-slate-600 mx-auto mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-slate-400 text-lg">No foods found</p>
                <p className="text-slate-600 text-sm mt-1">Try a different search term</p>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-4 px-4 text-slate-400 text-sm font-medium">Name</th>
                      <th className="text-right py-4 px-4 text-slate-400 text-sm font-medium">Calories</th>
                      <th className="text-right py-4 px-4 text-slate-400 text-sm font-medium">Protein</th>
                      <th className="text-right py-4 px-4 text-slate-400 text-sm font-medium">Carbs</th>
                      <th className="text-right py-4 px-4 text-slate-400 text-sm font-medium">Fats</th>
                      <th className="text-right py-4 px-4 text-slate-400 text-sm font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {foods.map((food) => (
                      <tr
                        key={food._id}
                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      >
                        <td className="py-3 px-4 text-white font-medium">{food.dish_name}</td>
                        <td className="py-3 px-4 text-right text-slate-300">
                          {Math.round(food.calories_kcal)} kcal
                        </td>
                        <td className="py-3 px-4 text-right text-blue-400">
                          {food.macros?.protein_g?.toFixed(1)}g
                        </td>
                        <td className="py-3 px-4 text-right text-amber-400">
                          {food.macros?.carbohydrates_g?.toFixed(1)}g
                        </td>
                        <td className="py-3 px-4 text-right text-rose-400">
                          {food.macros?.fat_total_g?.toFixed(1)}g
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEdit(food)}
                              className="px-3 py-1.5 bg-indigo-500/20 text-indigo-400 rounded-lg text-sm font-medium hover:bg-indigo-500/30 transition-colors"
                            >
                              Edit
                            </button>
                            {confirmDeleteId === food._id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleDelete(food._id)}
                                  disabled={deletingId === food._id}
                                  className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/30 transition-colors disabled:opacity-50"
                                >
                                  {deletingId === food._id ? '...' : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteId(null)}
                                  className="px-2 py-1.5 text-slate-400 rounded-lg text-sm hover:text-white transition-colors"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setConfirmDeleteId(food._id)}
                                className="px-3 py-1.5 bg-red-500/10 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/20 transition-colors"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-white/5">
                {foods.map((food) => (
                  <div key={food._id} className="p-4 hover:bg-white/5 transition-colors">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-white font-medium text-sm">{food.dish_name}</h3>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(food)}
                          className="px-3 py-1 bg-indigo-500/20 text-indigo-400 rounded-lg text-xs font-medium hover:bg-indigo-500/30 transition-colors"
                        >
                          Edit
                        </button>
                        {confirmDeleteId === food._id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(food._id)}
                              disabled={deletingId === food._id}
                              className="px-2 py-1 bg-red-500/20 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/30 transition-colors disabled:opacity-50"
                            >
                              {deletingId === food._id ? '...' : 'Yes'}
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="px-2 py-1 text-slate-400 rounded-lg text-xs hover:text-white transition-colors"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteId(food._id)}
                            className="px-2 py-1 bg-red-500/10 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/20 transition-colors"
                          >
                            Del
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <span className="text-slate-300">{Math.round(food.calories_kcal)} kcal</span>
                      <span className="text-blue-400">P: {food.macros?.protein_g?.toFixed(1)}g</span>
                      <span className="text-amber-400">C: {food.macros?.carbohydrates_g?.toFixed(1)}g</span>
                      <span className="text-rose-400">F: {food.macros?.fat_total_g?.toFixed(1)}g</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-[#12121a] border border-white/10 rounded-xl text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:border-indigo-500/50 transition-colors"
            >
              Previous
            </button>
            <span className="text-slate-400 text-sm px-4">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 bg-[#12121a] border border-white/10 rounded-xl text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:border-indigo-500/50 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editingFood && editForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={handleCancel}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg max-h-[85vh] overflow-y-auto glass-strong rounded-2xl border border-white/10 p-6"
            >
              <h2 className="text-xl font-bold text-white mb-6">Edit Food</h2>

              {/* Dish Name */}
              <div className="mb-4">
                <label className="block text-slate-400 text-sm mb-1">Dish Name</label>
                <input
                  type="text"
                  value={editForm.dish_name}
                  onChange={(e) => updateField('dish_name', e.target.value)}
                  className="w-full px-4 py-2.5 bg-black/50 border border-white/10 rounded-xl text-white focus:outline-none focus:border-indigo-500 transition-colors"
                />
              </div>

              {/* Calories */}
              <div className="mb-4">
                <label className="block text-slate-400 text-sm mb-1">Calories (kcal)</label>
                <input
                  type="number"
                  value={editForm.calories_kcal}
                  onChange={(e) => updateField('calories_kcal', e.target.value)}
                  className="w-full px-4 py-2.5 bg-black/50 border border-white/10 rounded-xl text-white focus:outline-none focus:border-indigo-500 transition-colors"
                />
              </div>

              {/* Macros Grid */}
              <div className="mb-4">
                <label className="block text-slate-400 text-sm mb-2">Macros (per 100g)</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-500 text-xs mb-1">Protein (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editForm.protein_g}
                      onChange={(e) => updateField('protein_g', e.target.value)}
                      className="w-full px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs mb-1">Carbohydrates (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editForm.carbohydrates_g}
                      onChange={(e) => updateField('carbohydrates_g', e.target.value)}
                      className="w-full px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs mb-1">Fat Total (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editForm.fat_total_g}
                      onChange={(e) => updateField('fat_total_g', e.target.value)}
                      className="w-full px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs mb-1">Sugars (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editForm.sugars_g}
                      onChange={(e) => updateField('sugars_g', e.target.value)}
                      className="w-full px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-slate-500 text-xs mb-1">Dietary Fiber (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editForm.dietary_fiber_g}
                      onChange={(e) => updateField('dietary_fiber_g', e.target.value)}
                      className="w-full px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Quantities */}
              {Object.keys(editForm.quantities).length > 0 && (
                <div className="mb-6">
                  <label className="block text-slate-400 text-sm mb-2">
                    Quantities (unit = grams)
                  </label>
                  <div className="space-y-2">
                    {Object.entries(editForm.quantities).map(([unit, grams]) => (
                      <div key={unit} className="flex items-center gap-3">
                        <span className="text-slate-300 text-sm w-32 truncate" title={unit}>
                          {unit}
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          value={grams}
                          onChange={(e) => updateQuantity(unit, e.target.value)}
                          className="flex-1 px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
                        />
                        <span className="text-slate-500 text-xs">g</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-slate-300 font-medium hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 bg-indigo-500 rounded-xl text-white font-medium hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default FoodDBPage;
